import { useSearchParams } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import { useSportsStandings, SPORT_LIST, SPORT_CONFIGS, SportType, getLegendItems } from "@/hooks/useSportsStandings";
import StandingsTable from "./components/StandingsTable";

export default function StandingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialSport = (searchParams.get("sport") as SportType) || "축구";
  const initialLeague = searchParams.get("league") || undefined;

  const {
    sportTab,
    setSportTab,
    leagueTab,
    setLeagueTab,
    displayRows,
    isLoading,
    hasError,
    useMock,
    currentSportConfig,
    rulesData,
    retryFetch,
  } = useSportsStandings(initialSport, initialLeague);

  const handleSportChange = (sport: SportType) => {
    setSportTab(sport);
    const defaultLeague = SPORT_CONFIGS[sport].defaultLeague;
    setLeagueTab(defaultLeague);
    setSearchParams({ sport, league: defaultLeague });
  };

  const handleLeagueChange = (league: string) => {
    setLeagueTab(league);
    setSearchParams({ sport: sportTab, league });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <main className="max-w-[1200px] mx-auto px-3 md:px-4 py-4 md:py-6">
        {/* Page Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-lg md:text-xl font-bold text-foreground-700 flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center text-primary-500">
              <i className="ri-trophy-line" />
            </span>
            종목별 리그 순위
          </h1>
          <p className="text-xs md:text-sm text-foreground-600 mt-1">
            네이버 스포츠 실시간 순위 데이터를 제공합니다
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden">
          {/* Sport Tabs */}
          <div className="flex items-center gap-1 px-3 md:px-4 pt-3 pb-2 border-b border-background-300">
            {SPORT_LIST.map((sport) => (
              <button
                key={sport}
                onClick={() => handleSportChange(sport)}
                className={`px-3 py-1.5 text-xs md:text-sm rounded font-medium whitespace-nowrap transition-colors flex-shrink-0 cursor-pointer ${
                  sportTab === sport
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-200 text-foreground-600 hover:bg-background-300"
                }`}
              >
                {sport}
              </button>
            ))}
          </div>

          {/* League Tabs */}
          {currentSportConfig.leagues.length > 1 && (
            <div className="flex items-center gap-1 px-3 md:px-4 py-2 border-b border-background-300 overflow-x-auto">
              {currentSportConfig.leagues.map((league) => (
                <button
                  key={league}
                  onClick={() => handleLeagueChange(league)}
                  className={`px-2.5 py-1 text-[11px] md:text-xs rounded whitespace-nowrap transition-colors flex-shrink-0 cursor-pointer ${
                    leagueTab === league
                      ? "bg-primary-500/20 text-primary-400"
                      : "text-foreground-600 hover:text-foreground-400"
                  }`}
                >
                  {league}
                </button>
              ))}
            </div>
          )}

          {/* Status Bar */}
          <div className="px-3 md:px-4 py-2 flex items-center justify-between border-b border-background-300/50 bg-background-50">
            <div className="flex items-center gap-2">
              <span className="text-xs md:text-sm font-bold text-foreground-700">{leagueTab} 순위</span>
              {useMock && !isLoading && (
                <span className="text-[10px] text-foreground-500 bg-background-200 px-1.5 py-0.5 rounded">Mock</span>
              )}
              {!useMock && !isLoading && displayRows.length > 0 && (
                <span className="text-[10px] text-accent-500 bg-accent-100/50 px-1.5 py-0.5 rounded">실시간</span>
              )}
            </div>
            {hasError && (
              <button
                onClick={retryFetch}
                className="text-[11px] text-primary-400 hover:text-primary-500 cursor-pointer whitespace-nowrap"
              >
                재시도
              </button>
            )}
          </div>

          {/* Table */}
          <StandingsTable
            displayRows={displayRows}
            sportTab={sportTab}
            leagueTab={leagueTab}
            isLoading={isLoading}
            rulesData={rulesData || {}}
          />

          {/* Legend */}
          {getLegendItems(leagueTab, rulesData || {}).length > 0 && (
            <div className="px-3 md:px-4 py-3 text-[10px] md:text-xs text-foreground-600 border-t border-background-300 flex flex-wrap gap-3">
              {getLegendItems(leagueTab, rulesData || {}).map((item) => (
                <span key={item.label} className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 ${item.color} rounded-sm flex-shrink-0`} />
                  {item.label}
                </span>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="px-3 md:px-4 py-2 text-[10px] md:text-xs text-foreground-600 border-t border-background-300">
            {useMock && !isLoading
              ? `네이버 스포츠 ${leagueTab} — 현재는 목업 데이터가 표시됩니다`
              : `네이버 스포츠 ${leagueTab} 실시간 데이터 — 30분마다 갱신`}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}